import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export const fetchUsersData = createAsyncThunk(
    'users/fetchUsers',
    async (params, { rejectWithValue }) => {
        try {
            // Add search_text to the API call
            const response = await apiService.getPaginatedUsers(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch users');
        }
    }
);

export const fetchUserRoleDropdown = createAsyncThunk(
    'maintenance/fetchUserRoleDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getUsersRole();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Users data');
        }
    }
);

export const createUser = createAsyncThunk(
    'users/createUser',
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiService.createUser(userData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create user');
        }
    }
);

export const deleteUser = createAsyncThunk(
    'users/deleteUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteUser(userId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete user');
        }
    }
);

export const getSingleUser = createAsyncThunk(
    'users/getSingleUser',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await apiService.getSingleUser(userId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch user data');
        }
    }
);

export const getUserDropdown = createAsyncThunk(
    'users/getUserDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getUserDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch user data');
        }
    }
);

export const editUser = createAsyncThunk(
    'users/editUser',
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiService.editUser(userData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to edit user');
        }
    }
);

export const resetDefaultPassword = createAsyncThunk(
    'users/resetDefaultPassword',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await apiService.resetDefaultPassword(userId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to reset password');
        }
    }
);

const initialState = {
    userRoles: [],
    userRoleLoading: false,
    data: [],
    loading: false,
    createLoading: false,
    deleteLoading: false,
    editLoading: false,
    singleUserLoading: false,
    resetPasswordLoading: false,
    userDropdownData: [],
    userDropdownLoading: false,
    singleUser: null,
    error: null,
    currentPage: 1,
    totalPages: 1,
};

const userSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearSingleUser: (state) => {
            state.singleUser = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsersData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsersData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload?.data?.results || [];
                state.meta = action.payload?.data?.meta || {};
                state.error = null;
            })
            .addCase(fetchUsersData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.data = [];
            })
            .addCase(fetchUserRoleDropdown.pending, (state) => {
                state.userRoleLoading = true;
                state.error = null;
            })
            .addCase(fetchUserRoleDropdown.fulfilled, (state, action) => {
                state.userRoleLoading = false;
                state.userRoles = action.payload?.data || [];
                state.error = null;
            })
            .addCase(fetchUserRoleDropdown.rejected, (state, action) => {
                state.userRoleLoading = false;
                state.error = action.payload;
                state.data = [];
            })
            .addCase(getUserDropdown.pending, (state) => {
                state.userDropdownLoading = true;
            })
            .addCase(getUserDropdown.fulfilled, (state, action) => {
                state.userDropdownLoading = false;
                state.userDropdownData = action.payload?.data || [];
            })
            .addCase(getUserDropdown.rejected, (state) => {
                state.userDropdownLoading = false;
                state.userDropdownData = [];
            })
            .addCase(createUser.pending, (state) => {
                state.createLoading = true;
                state.error = null;
            })
            .addCase(createUser.fulfilled, (state) => {
                state.createLoading = false;
                state.error = null;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload;
            })
            .addCase(deleteUser.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.error = null;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })
            .addCase(getSingleUser.pending, (state) => {
                state.singleUserLoading = true;
                state.error = null;
            })
            .addCase(getSingleUser.fulfilled, (state, action) => {
                state.singleUserLoading = false;
                state.singleUser = action.payload?.data || null;
                state.error = null;
            })
            .addCase(getSingleUser.rejected, (state, action) => {
                state.singleUserLoading = false;
                state.error = action.payload;
                state.singleUser = null;
            })
            .addCase(editUser.pending, (state) => {
                state.editLoading = true;
                state.error = null;
            })
            .addCase(editUser.fulfilled, (state, action) => {
                state.editLoading = false;
                state.error = null;
            })
            .addCase(editUser.rejected, (state, action) => {
                state.editLoading = false;
                state.error = action.payload;
            })
            .addCase(resetDefaultPassword.pending, (state) => {
                state.resetPasswordLoading = true;
                state.error = null;
            })
            .addCase(resetDefaultPassword.fulfilled, (state, action) => {
                state.resetPasswordLoading = false;
                state.error = null;
            })
            .addCase(resetDefaultPassword.rejected, (state, action) => {
                state.resetPasswordLoading = false;
                state.error = action.payload;
            });
    },
});

export const { setCurrentPage, clearError, clearSingleUser } = userSlice.actions;

export default userSlice.reducer;
